import React, { useCallback, useEffect, useMemo } from 'react';
import ReactFlow, {
    useNodesState,
    useEdgesState,
    addEdge,
    ConnectionLineType,
    MarkerType,
    Controls,
    Background,
    useReactFlow,
    ReactFlowProvider,
    Panel,
    Handle,
    Position
} from 'reactflow';
import 'reactflow/dist/style.css';
import dagre from 'dagre';
import { Box, Typography, Paper, Chip, Button, IconButton, useTheme } from '@mui/material';
import { Refresh as RefreshIcon, LinkOff as UnlinkIcon } from '@mui/icons-material';
import api from '../services/api';

const position = { x: 0, y: 0 };
const nodeWidth = 220;
const nodeHeight = 80;

const getLayoutedElements = (nodes, edges, direction = 'TB') => {
    const dagreGraph = new dagre.graphlib.Graph();
    dagreGraph.setDefaultEdgeLabel(() => ({}));

    dagreGraph.setGraph({ rankdir: direction, nodesep: 60, ranksep: 120, marginx: 40, marginy: 40 });

    nodes.forEach((node) => {
        dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
    });

    edges.forEach((edge) => {
        dagreGraph.setEdge(edge.source, edge.target);
    });

    dagre.layout(dagreGraph);

    const layoutedNodes = nodes.map((node) => {
        const nodeWithPosition = dagreGraph.node(node.id);
        // We are shifting the dagre node position (anchor=center center) to the top left
        // so it matches the React Flow node anchor point (top left).
        return {
            ...node,
            position: {
                x: nodeWithPosition.x - nodeWidth / 2,
                y: nodeWithPosition.y - nodeHeight / 2,
            },
        };
    });

    return { nodes: layoutedNodes, edges };
};

const CustomNode = ({ data, sx = {} }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const accent = data.accent || '#4f46e5';

    return (
        <Paper
            elevation={6}
            sx={{
                p: 1.75,
                pl: 2,
                minWidth: 200,
                textAlign: 'left',
                border: '1px solid',
                borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)',
                borderRadius: 2.5,
                background: isDark
                    ? `linear-gradient(135deg, rgba(30,41,59,0.95) 0%, rgba(15,23,42,0.95) 100%)`
                    : `linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)`,
                fontSize: '0.875rem',
                fontWeight: 700,
                color: 'text.primary',
                position: 'relative',
                overflow: 'visible',
                boxShadow: isDark
                    ? `0 8px 24px rgba(0,0,0,0.45), 0 0 0 1px ${accent}33`
                    : `0 8px 24px rgba(15,23,42,0.08), 0 0 0 1px ${accent}33`,
                transition: 'transform 160ms ease, box-shadow 160ms ease',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: isDark
                        ? `0 14px 32px rgba(0,0,0,0.55), 0 0 0 1px ${accent}66`
                        : `0 14px 32px rgba(15,23,42,0.14), 0 0 0 1px ${accent}66`,
                },
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 5,
                    borderTopLeftRadius: 10,
                    borderBottomLeftRadius: 10,
                    background: `linear-gradient(180deg, ${accent} 0%, ${accent}99 100%)`,
                },
                ...data.sx
            }}
        >
            {/* Input Handle (Top) */}
            <Handle type="target" position={Position.Top} style={{ background: accent, width: 10, height: 10, border: '2px solid #fff' }} />

            {/* Output Handle (Bottom) */}
            <Handle type="source" position={Position.Bottom} style={{ background: accent, width: 10, height: 10, border: '2px solid #fff' }} />

            {/* Additional Side Handles for flexible wiring if needed */}
            <Handle type="target" position={Position.Left} id="l" style={{ top: '50%', background: accent, width: 10, height: 10, border: '2px solid #fff' }} />
            <Handle type="source" position={Position.Right} id="r" style={{ top: '50%', background: accent, width: 10, height: 10, border: '2px solid #fff' }} />

            {/* Unlink Button (Top Right) */}
            {data.onUnlink && data.canUnlink && (
                <IconButton
                    size="small"
                    title="Unlink from process"
                    onMouseDown={(e) => { e.stopPropagation(); }}
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        data.onUnlink();
                    }}
                    sx={{
                        position: 'absolute',
                        top: -10,
                        right: -10,
                        bgcolor: '#ef4444',
                        color: 'white',
                        width: 24,
                        height: 24,
                        padding: 0,
                        border: '2px solid #fff',
                        '&:hover': { bgcolor: '#dc2626', transform: 'scale(1.1)' },
                        transition: 'transform 120ms ease',
                        zIndex: 20,
                        boxShadow: '0 4px 10px rgba(239,68,68,0.4)',
                        pointerEvents: 'all',
                    }}
                >
                    <UnlinkIcon sx={{ fontSize: 14 }} />
                </IconButton>
            )}

            {data.label}
            {data.subLabel && (
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                    {data.subLabel}
                </Typography>
            )}
            {data.ownerName && (
                <Box
                    title={`Owner: ${data.ownerName}`}
                    sx={{
                        position: 'absolute',
                        bottom: -8,
                        right: -6,
                        bgcolor: '#f59e0b',
                        color: '#fff',
                        fontSize: '0.6rem',
                        fontWeight: 700,
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 1,
                        maxWidth: 120,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        boxShadow: 1,
                        zIndex: 5,
                    }}
                >
                    {data.ownerName}
                </Box>
            )}
        </Paper>
    );
};

const nodeTypes = {
    custom: CustomNode,
};

// Local cache (mirrors the pattern used in OrganizationalChartPage)
const DOC_CHART_CACHE_KEY = 'doc_hierarchy_cache_v1';
const DOC_CHART_POSITIONS_CACHE_KEY = 'doc_hierarchy_positions_v1';

const saveChartCache = (key, data) => {
    try {
        localStorage.setItem(key, JSON.stringify({ timestamp: Date.now(), data }));
    } catch (error) {
        console.warn('Failed to save document chart cache:', error);
    }
};

const readChartCache = (key) => {
    try {
        const item = localStorage.getItem(key);
        return item ? JSON.parse(item) : null;
    } catch (error) {
        return null;
    }
};

function Flow({ processes, departments, documents, onLink, onUnlink }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    // Local cache: store the SOURCE arrays (not the derived nodes/edges, which contain functions
    // that don't survive JSON serialization and edge refs that can become stale).
    const cachedSource = React.useMemo(() => readChartCache(DOC_CHART_CACHE_KEY), []);
    const cachedPositions = React.useMemo(() => readChartCache(DOC_CHART_POSITIONS_CACHE_KEY), []);

    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const { fitView } = useReactFlow();
    const [reactFlowInstance, setReactFlowInstance] = React.useState(null);

    const [libraryItems, setLibraryItems] = React.useState({ processes: [], departments: [], documents: [] });
    const [savedPositions, setSavedPositions] = React.useState(cachedPositions?.data || {});
    // If positions are already cached locally, don't block the first paint on the /org-chart fetch.
    const [isRestoringPositions, setIsRestoringPositions] = React.useState(!cachedPositions?.data);

    // If the parent hasn't passed real data yet, fall back to cached source so the chart paints instantly.
    const effectiveProcesses = (processes && processes.length) ? processes : (cachedSource?.data?.processes || []);
    const effectiveDepartments = (departments && departments.length) ? departments : (cachedSource?.data?.departments || []);
    const effectiveDocuments = (documents && documents.length) ? documents : (cachedSource?.data?.documents || []);

    // Fetch saved positions on mount
    useEffect(() => {
        const fetchPositions = async () => {
            try {
                const response = await api.get('/org-chart');
                const positions = {};
                if (response.data) {
                    response.data.forEach(pos => {
                        positions[`${pos.entity_type === 'process' ? 'proc' : pos.entity_type === 'department' ? 'dept' : 'doc'}_${pos.entity_id}`] = { x: pos.position_x, y: pos.position_y };
                    });
                }
                setSavedPositions(positions);
                saveChartCache(DOC_CHART_POSITIONS_CACHE_KEY, positions);
                setIsRestoringPositions(false);
            } catch (error) {
                console.error("Failed to fetch chart positions", error);
                setIsRestoringPositions(false);
            }
        };
        fetchPositions();
    }, []);

    // Drag & Drop Handlers
    const onDragStart = (event, nodeType, item) => {
        event.dataTransfer.setData('application/reactflow', nodeType);
        event.dataTransfer.setData('itemData', JSON.stringify(item));
        event.dataTransfer.effectAllowed = 'move';
    };

    const onDragOver = useCallback((event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    }, []);

    const onDrop = useCallback(
        (event) => {
            event.preventDefault();

            const type = event.dataTransfer.getData('application/reactflow');
            const itemData = event.dataTransfer.getData('itemData');
            if (!type || !itemData) return;

            const item = JSON.parse(itemData);

            // Use the ReactFlow wrapper bounds for accurate flow-space coordinates.
            const wrapper = reactFlowInstance ? document.querySelector('.react-flow') : null;
            const bounds = wrapper ? wrapper.getBoundingClientRect() : { left: 0, top: 0 };
            const dropX = event.clientX - bounds.left;
            const dropY = event.clientY - bounds.top;

            // Find candidate parent nodes for a document drop: nearest by Euclidean distance
            // OR direct hit on a node (whichever comes first).
            if (type === 'document') {
                const candidates = nodes.filter(n =>
                    n.id.startsWith('proc_') || n.id.startsWith('dept_') ||
                    (n.id.startsWith('doc_') && n.id !== `doc_${item.id}`)
                );

                let chosen = null;
                let minDist = Infinity;
                candidates.forEach(n => {
                    const nx = (n.position?.x || 0) + nodeWidth / 2;
                    const ny = (n.position?.y || 0) + nodeHeight / 2;
                    // Direct hit (drop landed inside the node's box)
                    const dx = Math.abs(dropX - nx);
                    const dy = Math.abs(dropY - ny);
                    if (dx < nodeWidth / 2 + 40 && dy < nodeHeight / 2 + 40) {
                        const d = Math.hypot(dx, dy);
                        if (d < minDist) { minDist = d; chosen = n; }
                    }
                });
                // Fallback: nearest process if nothing was directly hit
                if (!chosen) {
                    candidates.filter(n => n.id.startsWith('proc_')).forEach(n => {
                        const nx = (n.position?.x || 0) + nodeWidth / 2;
                        const ny = (n.position?.y || 0) + nodeHeight / 2;
                        const d = Math.hypot(dropX - nx, dropY - ny);
                        if (d < minDist) { minDist = d; chosen = n; }
                    });
                }

                if (chosen && onLink) {
                    const parentType = chosen.id.startsWith('dept_') ? 'department' :
                        chosen.id.startsWith('doc_') ? 'document' : 'process';
                    const parentId = chosen.id.replace('dept_', '').replace('proc_', '').replace('doc_', '');
                    // Optimistically remove from library; the parent will refetch and re-render the chart.
                    setLibraryItems(prev => ({
                        ...prev,
                        documents: (prev.documents || []).filter(i => i.id !== item.id)
                    }));
                    onLink(item.id, parentType, parentId);
                }
                return;
            }

            // Process drag = no link, just add a temporary node so the user can drop a doc onto it.
            if (type === 'process') {
                const nodeId = `proc_${item.id}`;
                if (!nodes.find(n => n.id === nodeId)) {
                    setNodes(nds => nds.concat({
                        id: nodeId,
                        type: 'custom',
                        position: { x: dropX - nodeWidth / 2, y: dropY - nodeHeight / 2 },
                        data: { label: item.title || item.name, subLabel: 'PROCESS', accent: '#6366f1' },
                    }));
                    setLibraryItems(prev => ({
                        ...prev,
                        processes: (prev.processes || []).filter(i => i.id !== item.id)
                    }));
                }
            }
        },
        [setNodes, nodes, onLink, reactFlowInstance]
    );

    const processData = useCallback(() => {
        const initialNodes = [];
        const initialEdges = [];
        const inactive = { processes: [], departments: [], documents: [] };

        // Clean chart: ONLY show documents that are linked to a process, with their process and the owner's job position.
        const srcProcesses = effectiveProcesses;
        const srcDepartments = effectiveDepartments;
        const srcDocuments = effectiveDocuments;
        const linkedDocs = srcDocuments.filter(d => !!d.process_id);
        const referencedProcessIds = new Set(linkedDocs.map(d => d.process_id));

        // Process nodes — only those that actually have linked documents
        srcProcesses.forEach(proc => {
            if (!referencedProcessIds.has(proc.id)) return;
            initialNodes.push({
                id: `proc_${proc.id}`,
                type: 'custom',
                position,
                data: {
                    label: proc.title,
                    subLabel: 'PROCESS',
                    accent: '#6366f1',
                },
            });
        });

        // Document Nodes — only docs linked to a process
        linkedDocs.forEach(doc => {
            const docNodeId = `doc_${doc.id}`;
            const owner = doc.created_by_user;
            const ownerName = owner
                ? (owner.job_title
                    || (owner.job_position && (owner.job_position.title || owner.job_position.name))
                    || null)
                : null;
            initialNodes.push({
                id: docNodeId,
                type: 'custom',
                position,
                data: {
                    label: doc.title,
                    subLabel: `DOCUMENT · v${doc.current_version || 1}`,
                    ownerName,
                    accent: '#10b981',
                    onUnlink: () => onUnlink && onUnlink(doc.id),
                    canUnlink: true
                },
            });

            initialEdges.push({
                id: `e_proc_${doc.process_id}_${doc.id}`,
                source: `proc_${doc.process_id}`,
                target: docNodeId,
                type: 'smoothstep',
                animated: true,
                style: { stroke: '#6366f1', strokeWidth: 2.5 },
                markerEnd: { type: MarkerType.ArrowClosed, color: '#6366f1', width: 20, height: 20 },
            });
        });

        // Stash the rest in the library so the user can drag-link them
        srcDocuments.forEach(doc => { if (!doc.process_id) inactive.documents.push(doc); });
        srcProcesses.forEach(proc => { if (!referencedProcessIds.has(proc.id)) inactive.processes.push(proc); });

        const layouted = getLayoutedElements(initialNodes, initialEdges);

        // Override with saved positions if available
        const nodesWithSavedPositions = layouted.nodes.map(node => {
            if (savedPositions[node.id]) {
                const saved = savedPositions[node.id];
                // If saved position exists, use it. 
                // We might want to be smart: if auto-layout is vastly different, maybe notify?
                // For now, saved wins.
                return { ...node, position: { x: saved.x, y: saved.y } };
            }
            return node;
        });

        setNodes(nodesWithSavedPositions);
        setEdges(layouted.edges);
        setLibraryItems(inactive);

        // Persist the SOURCE arrays so the next visit can re-render instantly without re-fetching.
        if (srcDocuments.length > 0 || srcProcesses.length > 0) {
            saveChartCache(DOC_CHART_CACHE_KEY, {
                processes: srcProcesses,
                departments: srcDepartments,
                documents: srcDocuments,
            });
        }
    }, [effectiveProcesses, effectiveDepartments, effectiveDocuments, setNodes, setEdges, savedPositions]);

    useEffect(() => {
        if (!isRestoringPositions) {
            processData();
        }
    }, [processData, isRestoringPositions]);

    const onNodeDragStop = useCallback((event, node) => {
        // Save position to DB
        const type = node.id.startsWith('proc_') ? 'process' :
            node.id.startsWith('dept_') ? 'department' : 'document';
        const id = node.id.replace('proc_', '').replace('dept_', '').replace('doc_', '');

        api.post('/org-chart/position', {
            entity_type: type,
            entity_id: id,
            position_x: node.position.x,
            position_y: node.position.y
        }).catch(err => console.error("Failed to save node position", err));

        // Update local saved state so re-renders don't jump back
        setSavedPositions(prev => ({
            ...prev,
            [node.id]: { x: node.position.x, y: node.position.y }
        }));
    }, []);

    const onConnect = useCallback(
        (params) => {
            const { source, target } = params;

            // Allow Dept->Dept or Dept->Doc wiring visually
            // Check types
            const isSourceDept = source.startsWith('dept_');
            const isTargetDept = target.startsWith('dept_');
            const isTargetDoc = target.startsWith('doc_');
            const isSourceProc = source.startsWith('proc_');
            const isSourceDoc = source.startsWith('doc_');
            const isTargetProc = target.startsWith('proc_');

            // 1. Handle Document Linking (Persistence)
            // Case A: Parent -> Doc (Normal)
            if ((isSourceDept || isSourceProc || isSourceDoc) && isTargetDoc) {
                // If Doc->Doc, exclude self-loop or reverse if needed? 
                // We assume Source is Parent.
                if (source === target) return;

                const type = isSourceDept ? 'department' :
                    isSourceDoc ? 'document' : 'process';

                const sourceId = source.replace(isSourceDept ? 'dept_' : isSourceDoc ? 'doc_' : 'proc_', '');
                const docId = target.replace('doc_', '');

                console.log(`Linking ${type} ${sourceId} to Doc ${docId}`);
                if (onLink) {
                    onLink(docId, type, sourceId);
                }
            }
            // Case B: Doc -> Parent (Reverse)
            else if (isSourceDoc && (isTargetProc || isTargetDept)) {
                const type = isTargetDept ? 'department' : 'process';
                const sourceId = target.replace(isTargetDept ? 'dept_' : 'proc_', '');
                const docId = source.replace('doc_', '');

                console.log(`Linking ${type} ${sourceId} to Doc ${docId} (Reverse)`);
                if (onLink) {
                    onLink(docId, type, sourceId);
                }
            }

            // 2. Allow Dept->Dept Wiring (Visual only for now, per user request)
            if (isSourceDept && isTargetDept) {
                console.log(`Wired Department ${source} to ${target}`);
            }

            // Always add the visual edge for feedback
            setEdges((eds) => addEdge({
                ...params,
                type: 'smoothstep',
                animated: true,
                style: { stroke: '#2563eb', strokeWidth: 2 }
            }, eds));
        },
        [onLink, setEdges]
    );

    const handleReset = () => {
        processData();
        setTimeout(() => fitView(), 100);
    };

    // Render Library Component
    const renderLibrary = () => (
        <Paper
            elevation={0}
            sx={{
                width: 320,
                flexShrink: 0,
                borderLeft: '1px solid',
                borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'divider',
                p: 2.5,
                bgcolor: isDark ? 'rgba(15,23,42,0.6)' : 'background.paper',
                overflowY: 'auto',
                boxShadow: isDark ? '-4px 0 12px rgba(0,0,0,0.3)' : '-4px 0 12px rgba(0,0,0,0.05)',
                zIndex: 10,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
            }}
        >
            <Box>
                <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 800, letterSpacing: '-0.02em' }}>
                    Library
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                    Drag items onto the canvas to link them
                </Typography>
            </Box>

            <Box display="flex" flexDirection="column" gap={2}>
                {['processes', 'documents'].map(type => {
                    const isProcess = type === 'processes';
                    const isDept = type === 'departments';

                    return (
                        <Paper
                            key={type}
                            variant="outlined"
                            sx={{
                                flex: 1,
                                p: 2,
                                bgcolor: 'background.default',
                                borderColor: 'divider',
                                borderRadius: 2
                            }}
                        >
                            <Box display="flex" alignItems="center" gap={1} mb={2}>
                                <Box
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        bgcolor: isProcess ? '#4f46e5' : isDept ? '#0ea5e9' : '#10b981'
                                    }}
                                />
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.primary' }}>
                                    {type} <Chip label={libraryItems[type].length} size="small" sx={{ ml: 1, height: 20, fontSize: '0.7rem', bgcolor: 'action.selected', color: 'text.secondary', fontWeight: 'bold' }} />
                                </Typography>
                            </Box>

                            <Box display="flex" flexDirection="column" gap={1.5}>
                                {libraryItems[type].map(item => (
                                    <Paper
                                        key={item.id}
                                        draggable
                                        onDragStart={(event) => onDragStart(event, isProcess ? 'process' : isDept ? 'department' : 'document', item)}
                                        elevation={0}
                                        sx={{
                                            p: 1.5,
                                            cursor: 'grab',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1.5,
                                            bgcolor: 'background.paper',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                borderColor: isProcess ? '#4f46e5' : isDept ? '#0ea5e9' : '#10b981',
                                                boxShadow: isDark ? '0 4px 6px -1px rgba(0, 0, 0, 0.4)' : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                                transform: 'translateY(-1px)'
                                            },
                                            borderLeft: isProcess ? '4px solid #4f46e5' :
                                                isDept ? '4px solid #0ea5e9' : '4px solid #10b981'
                                        }}
                                    >
                                        <Box display="flex" alignItems="center" justifyContent="space-between">
                                            <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
                                                {item.title || item.name}
                                            </Typography>
                                        </Box>
                                    </Paper>
                                ))}
                                {libraryItems[type].length === 0 && (
                                    <Box sx={{ p: 2, textAlign: 'center', color: 'text.disabled', border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                                        <Typography variant="caption" sx={{ color: 'text.disabled' }}>All items active</Typography>
                                    </Box>
                                )}
                            </Box>
                        </Paper>
                    );
                })}
            </Box>
        </Paper>
    );

    const linkedDocCount = nodes.filter(n => n.id.startsWith('doc_')).length;
    const linkedProcCount = nodes.filter(n => n.id.startsWith('proc_')).length;

    return (
        <Box sx={{
            width: '100%',
            height: 800,
            border: '1px solid',
            borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'divider',
            borderRadius: 3,
            display: 'flex',
            flexDirection: 'row',
            overflow: 'hidden',
            boxShadow: isDark ? '0 12px 40px rgba(0,0,0,0.5)' : '0 12px 40px rgba(15,23,42,0.08)',
        }}>
            <Box sx={{
                flex: 1,
                minWidth: 0,
                position: 'relative',
                background: isDark
                    ? 'radial-gradient(ellipse at top, #1e293b 0%, #0f172a 70%)'
                    : 'radial-gradient(ellipse at top, #f8fafc 0%, #eef2ff 70%)',
            }}>
                <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    onConnect={onConnect}
                    onInit={setReactFlowInstance}
                    onDrop={onDrop}
                    onDragOver={onDragOver}
                    onNodeDragStop={onNodeDragStop}
                    nodeTypes={nodeTypes}
                    fitView
                    fitViewOptions={{ padding: 0.25 }}
                    proOptions={{ hideAttribution: true }}
                    defaultEdgeOptions={{ type: 'smoothstep' }}
                >
                    <Controls showInteractive={false} style={{ borderRadius: 8, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }} />
                    <Background color={isDark ? '#334155' : '#cbd5e1'} gap={20} size={1.5} />
                    <Panel position="top-left">
                        <Box sx={{
                            display: 'flex',
                            gap: 1,
                            px: 2,
                            py: 1,
                            borderRadius: 2,
                            bgcolor: isDark ? 'rgba(15,23,42,0.85)' : 'rgba(255,255,255,0.85)',
                            backdropFilter: 'blur(8px)',
                            border: '1px solid',
                            borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        }}>
                            <Chip size="small" label={`${linkedProcCount} Process${linkedProcCount === 1 ? '' : 'es'}`} sx={{ bgcolor: '#6366f1', color: '#fff', fontWeight: 700 }} />
                            <Chip size="small" label={`${linkedDocCount} Document${linkedDocCount === 1 ? '' : 's'}`} sx={{ bgcolor: '#10b981', color: '#fff', fontWeight: 700 }} />
                        </Box>
                    </Panel>
                    <Panel position="top-right">
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<RefreshIcon />}
                            onClick={handleReset}
                            sx={{
                                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                                boxShadow: '0 4px 12px rgba(99,102,241,0.4)',
                                textTransform: 'none',
                                fontWeight: 700,
                                '&:hover': { background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' },
                            }}
                        >
                            Reset Layout
                        </Button>
                    </Panel>
                    {nodes.length === 0 && (
                        <Panel position="top-center">
                            <Box sx={{
                                mt: 12,
                                px: 4,
                                py: 3,
                                borderRadius: 3,
                                bgcolor: isDark ? 'rgba(15,23,42,0.85)' : 'rgba(255,255,255,0.85)',
                                backdropFilter: 'blur(8px)',
                                border: '1px dashed',
                                borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(15,23,42,0.15)',
                                textAlign: 'center',
                                maxWidth: 400,
                            }}>
                                <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 700, mb: 0.5 }}>
                                    No linked documents yet
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    Drag a document from the library below onto a process to start building your hierarchy.
                                </Typography>
                            </Box>
                        </Panel>
                    )}
                </ReactFlow>
            </Box>
            {/* Bottom Library Panel */}
            {renderLibrary()}
        </Box>
    );
}

export default function DocumentHierarchyFlow(props) {
    return (
        <ReactFlowProvider>
            <Flow {...props} />
        </ReactFlowProvider>
    );
}
